import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import {
  clientLibraryDiscoveryPanelLoad,
  clientLibraryDiscoveryPanelSetLoad,
} from '@disco/state-management/actions/client-library-discovery-panel-actions'
import { getDiscoveryPanelSet } from '@disco/state-management/selectors/client-libraries'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type { DiscoveryPanelItem } from '../utils'

export type ApiFilterValue = '' | 'all' | 'approved' | 'is_favorite'
export type FilterValue = string

const API_FILTERS: readonly ApiFilterValue[] = [
  '',
  'all',
  'approved',
  'is_favorite',
]

// Anything outside API_FILTERS is a category name, filtered locally.
export const isGroupFilter = (value: string): boolean =>
  !API_FILTERS.includes(value as ApiFilterValue)

export const parseApiFilterParam = (
  rawValue: string | null,
): ApiFilterValue | null =>
  rawValue && API_FILTERS.includes(rawValue as ApiFilterValue)
    ? (rawValue as ApiFilterValue)
    : null

export const useDiscoverySearchFilterCategories = (): string[] => {
  const dispatch = useDispatch()
  const discoveryPanelSet = useSelector(getDiscoveryPanelSet)
  const hasRequestedSet = useRef(false)

  useEffect(() => {
    // A failed load leaves loading and loaded false, which would re-dispatch.
    if (hasRequestedSet.current) return

    if (!discoveryPanelSet.loading && !discoveryPanelSet.loaded) {
      hasRequestedSet.current = true
      dispatch(clientLibraryDiscoveryPanelSetLoad())
    }
  }, [dispatch, discoveryPanelSet.loading, discoveryPanelSet.loaded])

  return useMemo(() => {
    if (!discoveryPanelSet.loaded || !Array.isArray(discoveryPanelSet.items)) {
      return []
    }
    return (discoveryPanelSet.items as DiscoveryPanelItem[])
      .filter(
        item =>
          item.name &&
          !item.name.toLowerCase().includes('[draft]') &&
          !item.name.toLowerCase().includes('featured'),
      )
      .map(item => item.name)
  }, [discoveryPanelSet])
}

type UseDiscoverySearchFiltersOptions = {
  // Mirrored into the URL alongside the filter.
  query: string
  // For callers with a results list to reset loading state and refetch.
  onApiFilterSelected?: (_value: ApiFilterValue) => void
  onGroupFilterSelected?: (_value: string) => void
  // Refetch when an API filter arrives via the URL.
  loadOnUrlFilter?: boolean
}

export const useDiscoverySearchFilters = ({
  query,
  onApiFilterSelected,
  onGroupFilterSelected,
  loadOnUrlFilter = false,
}: UseDiscoverySearchFiltersOptions) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Empty string means "no filter set"; API calls fall back to 'all'.
  const [filter, setFilter] = useState<ApiFilterValue>('')
  const [groupFilter, setGroupFilter] = useState<string>('')

  useEffect(() => {
    const filterParam = parseApiFilterParam(searchParams.get('filter'))
    const groupParam = searchParams.get('group')
    const q = searchParams.get('q') || ''

    if (filterParam) {
      setFilter(filterParam)
      if (!groupParam && loadOnUrlFilter) {
        dispatch(clientLibraryDiscoveryPanelLoad(q, filterParam))
      }
    } else if (!groupParam) {
      setFilter('')
    }

    setGroupFilter(groupParam || '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const handleFilterChange = useCallback(
    (value: FilterValue) => {
      const isGroup = isGroupFilter(value)

      if (isGroup) {
        setGroupFilter(value)
        // Group filtering is local, so the API filter goes back to unfiltered.
        setFilter('all')
        onGroupFilterSelected?.(value)
      } else {
        setFilter(value as ApiFilterValue)
        setGroupFilter('')

        if (filter !== value) {
          onApiFilterSelected?.(value as ApiFilterValue)
        }
      }

      const params = new URLSearchParams()
      if (query) params.set('q', query)
      if (isGroup) {
        params.set('group', value)
      } else if (value) {
        params.set('filter', value)
      }
      // State preserves search context across the URL change.
      navigate(`/discover/search?${params.toString()}`, {
        state: {
          query,
          filter: value || 'all',
          searchLocation: location.pathname,
        },
      })
    },
    [
      query,
      navigate,
      location,
      onGroupFilterSelected,
      filter,
      onApiFilterSelected,
    ],
  )

  const clearFilters = useCallback(() => {
    setFilter('')
    setGroupFilter('')
  }, [])

  return {
    filter,
    groupFilter,
    // Group filter shadows the API filter in the header.
    activeFilter: groupFilter || filter,
    setFilter,
    setGroupFilter,
    handleFilterChange,
    clearFilters,
  }
}
